import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useAtom } from "jotai";
import { z } from "zod";

import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { WelcomeMeetingFooter } from "@/components/onboarding/welcome-meeting-footer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page, PageTitle } from "@/components/ui/layout/page/page";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
} from "@/components/ui/select-input/card-select";
import { welcomeMeetingAtom } from "@/lib/atoms/welcome-meeting-atom";

import type { JSX } from "react";

const welcomeMeetingFormatSearchSchema = z.object({
  action: z.string().optional(),
});

const WelcomeMeetingFormatPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { flags } = useRouteContext({ from: "__root__" });
  const [form, setForm] = useAtom(welcomeMeetingAtom);

  const showOnlineOption = Boolean(flags["ativa-agendamento-de-onboarding-online"]);

  const handleFormatChange = (value: string): void => {
    setForm((prev) => ({
      ...prev,
      visitType: value as "presential" | "online",
    }));
  };

  const handleContinue = (): void => {
    void navigate({ to: "/onboarding/welcome-meeting/location" });
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={1 / 3} />
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-4">
          <div>
            <PageTitle>Qual formato de reunião você prefere?</PageTitle>
          </div>

          <CardSelectRoot
            name="meeting-format"
            mode="single"
            defaultValue={form.visitType || undefined}
            onChange={handleFormatChange}
          >
            <CardSelectItemContainer>
              <CardSelectItem value="presential">Presencial</CardSelectItem>
              {showOnlineOption ? (
                <CardSelectItem value="online">Online</CardSelectItem>
              ) : null}
            </CardSelectItemContainer>
          </CardSelectRoot>
        </div>

        <div className="mt-auto">
          <WelcomeMeetingFooter onSubmit={handleContinue} disabled={!form.visitType} />
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/format")({
  validateSearch: welcomeMeetingFormatSearchSchema,
  component: WelcomeMeetingFormatPage,
  beforeLoad: ({ context }) => {
    const showOnlineOption = Boolean(
      context.flags["ativa-agendamento-de-onboarding-online"],
    );

    if (!showOnlineOption) {
      redirect({ to: "/onboarding/welcome-meeting/location", throw: true });
    }
  },
});
